import {
  AuthenticationResult,
  AuthError,
  AuthErrorCodes,
  EndSessionRequest,
  EventType,
  InteractionRequiredAuthError,
  ProtocolMode,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { StringDict } from '@azure/msal-common';
import { Logger } from '@rhim/logging';
import { assert, isDefined, joinPaths } from '@rhim/utils';
import { Deferred } from '@rhim/utils/deferred';

import type { AuthConfiguration, BearerAccessToken, RhimAccountInfo } from './types';

const log = new Logger({
  context: 'Authentication',
  logLevel: 'info',
  format: (message, _, context) => {
    if (isDefined(context)) {
      return [`[${context}]`, ...message];
    }

    return message;
  },
});

/**
 * According to the typings of the AccountInfo interface, the username is a required property.
 * While this is the case in AAD and OIDC implementations, the restriction is only satisfied by setting the username to an empty string.
 * This renders this property completely useless, which is why we don't expose it anymore (see #33560).
 * If you want to display the user's name, use the `name` instead.
 *
 * **Update June 2nd, 2021**. It seems like `username` does contain the e-mail address now.
 */
export interface AuthInfo extends Omit<RhimAccountInfo, 'username'> {
  accessToken: string;

  /**
   * Provides the bearer token for backend communication in the form of `Bearer <accessToken>`
   * Can be directly used as value for the Authorization header
   */
  bearerAuthentication: BearerAccessToken;

  /**
   * The user's name. If the user's name is not available, then it contains their e-mail address instead
   */
  name: string;
}

interface AccessTokenInfo {
  secret: string;
  bearerAuthentication: BearerAccessToken;
  expiresOn: Date | null;
}

interface InterceptorsManager {
  add<T extends keyof InterceptorsMap>(event: T, interceptor: Unpack<InterceptorsMap[T]>): void;
  eject<T extends keyof InterceptorsMap>(event: T, interceptor: Unpack<InterceptorsMap[T]>): boolean;
}

interface InterceptorsMap {
  onBeforeLogout?: Set<(logoutRequest?: EndSessionRequest) => Promise<void>>;
  onBeforeAquireTokenRedirect?: Set<(request: RedirectRequest) => Promise<void>>;
  onBeforeLoginRedirect?: Set<(request?: RedirectRequest) => Promise<void>>;
}
/**
 * Read the basename under which the application is hosted
 */
export function getBasename(): string {
  if (typeof document !== 'undefined') {
    return new URL(document.baseURI).pathname;
  }

  return '/';
}

export const ACCESS_TOKEN_EXPIRY_THRESHOLD_MS = 30000;

/**
 * @param type https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#Authentication_schemes
 */
export function createAuthorizationHeader(type: 'Bearer', token: string): BearerAccessToken {
  return `${type} ${token}` as const;
}

const defaultRedirectUri = '/signedin';

export class AuthClient extends PublicClientApplication {
  static clientId: string;

  /**
   * as soon as the user is logged in we set up an automatic token renewal which runs right in time before the token expires. This allows silent renewal of the access token without requiring the user to login again
   */
  private automaticTokenRenewalTimer: NodeJS.Timeout | undefined;

  constructor(configuration: AuthConfiguration) {
    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const authority = configuration.authority.startsWith('/') ? new URL(configuration.authority, origin).href : configuration.authority;

    super({
      auth: {
        clientId: configuration.clientId,
        authority: `${authority}${configuration.loginPolicy}`,
        redirectUri: joinPaths(getBasename(), configuration.authRedirectUri ?? defaultRedirectUri), // take basename into account
        knownAuthorities: [`${authority}${configuration.loginPolicy}`],
        protocolMode: configuration.authProtocolMode === 'OIDC' ? ProtocolMode.OIDC : ProtocolMode.AAD,
      },
      cache: {
        cacheLocation: 'localStorage',
        // When @azure/msal-browser@^2.8.0 is used and this is enabled, the login redirect never happens.
        storeAuthStateInCookie: false,
      },
      system: {
        // We need this for E2E tests and Storybook, where the app runs in an iframe.
        allowRedirectInIframe: true,
      },
    });

    AuthClient.clientId = configuration.clientId; // there can only be one instance of the AuthClient. The clientId indicates which application that is

    this.scopes = configuration.scopes;
    this.usePopup = configuration.usePopup ?? false;
    this.appName = configuration.appName;
    this.redirectUri = configuration.authRedirectUri ?? defaultRedirectUri;

    if (isDefined(configuration.debug)) {
      log.debug('Initializing authentication with the following configuration:', configuration);
    }
  }

  static defaultScopes: string[] = ['offline_access'];

  public scopes: string[];
  public appName?: string;
  public redirectUri: string;
  protected usePopup: boolean;

  /**
   * Optional callback invoked whenever a new access token is acquired or refreshed.
   */
  public onTokenRefresh?: (token: AccessTokenInfo) => void;
  private authenticationInProgress: undefined | Deferred<AuthInfo>;
  private redirectHandlingInProgress: undefined | Deferred<AuthenticationResult | null>;
  /**
   * `rehydrate()` can be triggered by multiple callers at once (route loaders, axios interceptors, etc.).
   * If we allow concurrent executions, we may trigger multiple silent token requests and, on failure,
   * multiple redirects that overwrite each other. The last redirect often drops the intended return URL
   * and sends users to the splash screen. We therefore dedupe rehydrate calls and share a single promise.
   */
  private rehydrationInProgress: undefined | Deferred<AuthInfo>;
  private crossTabLogoutEnabled = false;
  protected interceptorsMap: InterceptorsMap = {};
  public interceptors: InterceptorsManager = {
    add: <T extends keyof InterceptorsMap>(event: T, interceptor: Unpack<InterceptorsMap[T]>) => {
      if (isDefined(this.interceptorsMap[event])) {
        // @ts-expect-error There is no way to model this in TypeScript.
        this.interceptorsMap[event].add(interceptor);
      } else {
        // @ts-expect-error There is no way to model this in TypeScript.
        this.interceptorsMap[event] = new Set([interceptor]);
      }
    },
    eject: (event, interceptor) => {
      // @ts-expect-error There is no way to model this in TypeScript.
      return this.interceptorsMap[event]?.delete(interceptor) ?? false;
    },
  };

  /**
   * Access token information
   * Set when the user is authenticated and automatically refreshed
   * */
  #accessToken: AccessTokenInfo = {
    secret: '',
    bearerAuthentication: '' as BearerAccessToken,
    expiresOn: null,
  };

  /** Synchronously gets the cached access token information */
  getAccessToken(): AccessTokenInfo {
    return this.#accessToken;
  }

  /**
   * Attempt to silently refresh the token without prompting the user to login
   * If a silent renewal is not possible, we actively authenticate the user
   */
  async renewToken(): Promise<AuthInfo> {
    const account = this.getActiveAccount();

    if (isDefined(account)) {
      log.debug('renewToken using active account');
      // acquire and return the auth info for the given user
      return this.acquireAuthInfo(account as RhimAccountInfo);
    }

    // if there's no account it means that the user is not logged in yet.
    // In this case, we have to log in the user from scratch
    log.error(`Silent token renewal not possible. Login required.`);

    try {
      const authInfo = await this.authenticate();
      return authInfo;
    } catch (error) {
      log.error(error);
      throw error;
    }
  }

  async acquireAuthInfo(account: RhimAccountInfo, redirectStartPage?: string): Promise<AuthInfo> {
    const extraQueryParameters = this.getExtraQueryParameters();
    const request: SilentRequest = {
      account,
      scopes: this.scopes,
      extraQueryParameters,
    };

    try {
      log.debug('acquiring token silently');
      const token = await this.acquireTokenSilent(request);
      assert(isDefined(token), 'Silently acquiring token failed. User interaction is required to proceed');
      assert(token.tokenType === 'Bearer', 'Expected token type to be a "Bearer" token');

      const bearerAuthentication: BearerAccessToken = `${token.tokenType} ${token.accessToken}`;

      const previousToken = this.#accessToken.secret;

      this.#accessToken = {
        secret: token.accessToken,
        expiresOn: token.expiresOn,
        bearerAuthentication,
      };

      if (token.accessToken !== previousToken) {
        this.onTokenRefresh?.(this.#accessToken);
      }

      // set up automatic token renewal before the token expires
      if (isDefined(token.expiresOn)) {
        this.clearAutomaticTokenRenewalTimer();
        const timeUntilExpiryThreshold = token.expiresOn.getTime() - Date.now() - ACCESS_TOKEN_EXPIRY_THRESHOLD_MS;
        log.debug('scheduling token renewal', {
          expiresOn: token.expiresOn.toISOString(),
          inMs: timeUntilExpiryThreshold,
        });
        this.automaticTokenRenewalTimer = setTimeout(() => {
          this.renewToken();
        }, timeUntilExpiryThreshold); // schedule at the expiry threshold time
      }

      const tokenAccount = token.account as RhimAccountInfo;

      return {
        ...tokenAccount,
        name: this.getAccountName(tokenAccount),
        accessToken: token.accessToken,
        bearerAuthentication, // provide bearer token that can be used for backend authentication
      };
    } catch (error) {
      log.error(`AuthClient: silent renew failed. Error type: "${error instanceof Error ? error.constructor.name : 'unknown'}". Reason:`, error);
      if (error instanceof InteractionRequiredAuthError) {
        // fallback to interaction when silent call fails
        // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/acquire-token.md#acquiring-an-access-token
        log.debug(
          'Calling acquireTokenSilent failed. Falling back to interaction now',
          'See https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/a4073eb20fcc7fb42f7858cf16998e7e9b74a411/lib/msal-browser/docs/acquire-token.md#acquiring-an-access-token'
        );

        // If silent auth fails we must redirect, but we *must* preserve the current URL (or the
        // caller-provided one) so the app can restore the correct page after login.
        const redirectRequest: RedirectRequest = {
          ...request,
          redirectStartPage: redirectStartPage ?? window.location.href,
        };

        return (await this.acquireTokenRedirect(redirectRequest)) as unknown as AuthInfo; // when this is called we'll navigate away from the page and the return value will never be assigned (this is why void is used in the typings)
      }

      throw error; // if silent renew failed and the user wasn't redirected to the login page, then we need to throw an error since we couldn't login the user
    }
  }

  private getAccountName(account: RhimAccountInfo): string {
    // #33560: the primary display name of the user should be their "name".
    // If the account name is not set, then we fall back to the preferred_username, which will be the user's e-mail address.
    // As last fallback, we will use the username
    return account.name ?? account.idTokenClaims?.preferred_username ?? account.username;
  }

  getExtraQueryParameters(): StringDict | undefined {
    const queryParams = new URLSearchParams(window.location.search);
    const loginHint = queryParams.get('login_hint');
    const domainHint = queryParams.get('domain_hint');
    const extraQueryParams: StringDict = {};

    if (isDefined(this.appName)) {
      extraQueryParams['appName'] = this.appName;
    }
    if (isDefined(domainHint)) {
      extraQueryParams['domain_hint'] = domainHint;
    }
    if (isDefined(loginHint)) {
      extraQueryParams['login_hint'] = loginHint;
    }

    return Object.keys(extraQueryParams).length === 0 ? undefined : extraQueryParams;
  }

  async authenticate(urlAfterLogin?: string): Promise<AuthInfo> {
    if (isDefined(this.authenticationInProgress)) {
      log.debug('authenticate reused in-progress flow');
      return this.authenticationInProgress.promise;
    }

    this.authenticationInProgress = new Deferred();

    try {
      const account = this.getActiveAccount();

      const requestParameters: RedirectRequest = {
        scopes: this.scopes,
        extraQueryParameters: this.getExtraQueryParameters(),
        redirectStartPage: urlAfterLogin,
      };

      if (isDefined(account)) {
        try {
          log.debug('authenticate with active account');
          const authInfo = await this.acquireAuthInfo(account as RhimAccountInfo, urlAfterLogin);
          return this.authenticationInProgress.resolve(authInfo);
        } catch {
          if (this.usePopup) {
            log.debug('Calling acquireAuthInfo failed. Popup flow was chosen as fallback. Redirecting now');

            await this.acquireTokenPopup(requestParameters); // await the popup
          } else {
            log.debug('Calling acquireAuthInfo failed. Redirect flow was chosen as fallback. Redirecting now');

            await this.acquireTokenRedirect(requestParameters); // we can't await this promise because this navigates away from the page
          }
        }
      } else {
        if (this.usePopup) {
          log.debug('no active account, starting loginPopup');
          await this.loginPopup(requestParameters);
        } else {
          log.debug('no active account, starting loginRedirect');
          await this.loginRedirect(requestParameters);
        }
      }

      return this.authenticationInProgress.reject("User couldn't be logged in.");
    } catch (error: unknown) {
      log.error(`authentication failed. Reason`, error);
      assert(error instanceof AuthError, 'Assumed MSAL throws only instances of AuthError');
      return this.authenticationInProgress.reject(error);
    }
  }

  /**
   * Silently acquire the cached access token if available
   * @param urlAfterRehydrate Url to go to after rehydrate.
   * @param onSuccess callback which is invoked once the auth info has been retrieved (only called if auth info is available)
   * @param onFailure error callback
   */
  async rehydrate(urlAfterRehydrate?: string): Promise<AuthInfo> {
    if (isDefined(this.rehydrationInProgress)) {
      log.debug('rehydrate reused in-progress flow');
      return this.rehydrationInProgress.promise;
    }

    this.rehydrationInProgress = new Deferred<AuthInfo>();

    try {
      if (isDefined(this.redirectHandlingInProgress)) {
        await this.redirectHandlingInProgress.promise;
      }

      const account = this.getActiveAccount();

      // If we have the user's account info we also try to acquire the authentication info, so that we can already utilize it
      if (isDefined(account)) {
        const authInfo = await this.acquireAuthInfo(account as RhimAccountInfo, urlAfterRehydrate ?? window.location.href);
        return this.rehydrationInProgress.resolve(authInfo);
      }

      throw new AuthError(AuthErrorCodes.unexpectedError, 'Could not acquire auth info because no account was found.');
    } catch (error) {
      assert(error instanceof AuthError, 'Assumed MSAL throws only instances of AuthError');

      return this.rehydrationInProgress.reject(error);
    } finally {
      this.rehydrationInProgress = undefined;
    }
  }

  /**
   * This should be invoked on all page loads involved in redirect auth flows.
   * @returns Token response or null. If the return value is null, then no auth redirect was detected.
   */
  async handleRedirect() {
    if (isDefined(this.redirectHandlingInProgress)) {
      return this.redirectHandlingInProgress.promise;
    }
    this.redirectHandlingInProgress = new Deferred();
    try {
      await this.initialize();
      const result = await this.handleRedirectPromise();
      if (result != null) {
        this.setActiveAccount(result.account);
        log.debug('redirect handled, active account set');
      } else {
        log.debug('no redirect result detected on handleRedirect');
      }
      return this.redirectHandlingInProgress.resolve(result);
    } catch (error) {
      return this.redirectHandlingInProgress.reject(error);
    } finally {
      this.redirectHandlingInProgress = undefined;
    }
  }

  async logout(logoutRequest?: EndSessionRequest): Promise<void> {
    this.clearAutomaticTokenRenewalTimer();

    this.#accessToken = {
      secret: '',
      bearerAuthentication: '' as BearerAccessToken,
      expiresOn: null,
    };

    if (this.interceptorsMap.onBeforeLogout) {
      for (const interceptor of this.interceptorsMap.onBeforeLogout) {
        await interceptor(logoutRequest);
      }
    }

    if (this.usePopup) {
      await super.logoutPopup(logoutRequest);
    } else {
      await super.logoutRedirect(logoutRequest);
    }
  }

  /**
   * Adds event listener for logout action in a different browser tab or window
   * which triggers the logout process in the current tab.
   */
  enableCrossTabLogout(postLogoutRedirectUri: string) {
    if (this.crossTabLogoutEnabled) {
      return;
    }
    this.enableAccountStorageEvents();
    this.addEventCallback((event) => {
      if (event.eventType === EventType.ACCOUNT_REMOVED) {
        void this.logout({ postLogoutRedirectUri });
      }
    });
    this.crossTabLogoutEnabled = true;
  }

  async acquireTokenRedirect(request: RedirectRequest) {
    // call and await all interceptors
    if (this.interceptorsMap.onBeforeAquireTokenRedirect) {
      for (const interceptor of this.interceptorsMap.onBeforeAquireTokenRedirect) {
        await interceptor(request);
      }
    }

    log.info('Acquiring token via redirect');

    await super.acquireTokenRedirect(request);
  }

  async loginRedirect(request?: RedirectRequest): Promise<void> {
    // call and await all interceptors
    if (this.interceptorsMap.onBeforeLoginRedirect) {
      for (const interceptor of this.interceptorsMap.onBeforeLoginRedirect) {
        await interceptor(request);
      }
    }

    await super.loginRedirect(request);
  }

  private clearAutomaticTokenRenewalTimer() {
    clearTimeout(this.automaticTokenRenewalTimer);
  }
}
